import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";

const ADMIN_EMAIL = "parmjeetgadwal@gmail.com";

const S = {
  page: { minHeight:"100vh", background:"#050510", color:"#fff", fontFamily:"'DM Sans',sans-serif", padding:32 },
  card: { background:"#0d0d1f", border:"1px solid #ffffff0d", borderRadius:16, padding:24, marginBottom:16 },
  stat: { background:"linear-gradient(135deg,#0d0d1f,#0f0f22)", border:"1px solid #ffffff0d", borderRadius:16, padding:"20px 24px" },
  inp:  { width:"100%", background:"#050510", border:"1px solid #ffffff22", borderRadius:10, padding:"13px 16px", color:"#fff", fontSize:15, outline:"none", boxSizing:"border-box", fontFamily:"'DM Sans',sans-serif" },
  btn:  { width:"100%", background:"linear-gradient(135deg,#00d4aa,#00b894)", color:"#050510", border:"none", borderRadius:12, padding:14, fontWeight:700, fontSize:16, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" },
  lbl:  { display:"block", color:"#9ca3af", fontSize:12, marginBottom:8, textTransform:"uppercase", letterSpacing:1 },
};

export default function Admin() {
  const [authed, setAuthed]   = useState(false);
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [stats, setStats]     = useState(null);
  const [users, setUsers]     = useState([]);
  const [msg, setMsg]         = useState("");
  const navigate = useNavigate();

  // Check if already logged in as admin
  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedEmail = localStorage.getItem("email");
    if (token && savedEmail === ADMIN_EMAIL) {
      loadAdminData();
    }
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      await API.get("/auth/check-admin");
      const [sr, ur] = await Promise.all([
        API.get("/admin/stats"),
        API.get("/admin/users"),
      ]);
      setStats(sr.data);
      setUsers(ur.data.users || []);
      setAuthed(true);
    } catch(e) {
      setError("Access denied. Admin only.");
      localStorage.clear();
    }
    setLoading(false);
  };

  const login = async () => {
    if (!email || !password) { setError("Fill in all fields."); return; }
    if (email.toLowerCase().trim() !== ADMIN_EMAIL) {
      setError("Access restricted to admin only.");
      return;
    }
    setLoading(true); setError("");
    try {
      const res = await API.post("/login", { email: email.trim().toLowerCase(), password });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("email", res.data.email);
      await loadAdminData();
    } catch(e) {
      setError(e?.response?.data?.detail || "Login failed.");
    }
    setLoading(false);
  };

  const logout = () => {
    localStorage.clear();
    setAuthed(false);
    setStats(null);
    setUsers([]);
  };

  const upgradePlan = async (userEmail, plan) => {
    try {
      await API.post("/admin/upgrade-user", { email: userEmail, plan });
      setUsers(us => us.map(u => u.email === userEmail ? {...u, plan} : u));
      setMsg(`✅ ${userEmail} → ${plan}`);
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("❌ Failed"); }
  };

  // ── Login Screen ────────────────────────────────────────────────────────────
  if (!authed) return (
    <div style={{ ...S.page, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700&family=Syne:wght@900&display=swap" rel="stylesheet"/>
      <div style={{ width:"100%", maxWidth:400 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:26, fontWeight:900, color:"#00d4aa", marginBottom:8 }}>⚡ LeadBot Pro</div>
          <div style={{ background:"#f59e0b22", border:"1px solid #f59e0b44", borderRadius:10, padding:"8px 16px", display:"inline-block", fontSize:13, color:"#f59e0b", fontWeight:700 }}>
            🔐 Admin Access Only
          </div>
          <p style={{ color:"#6b7280", marginTop:12, fontSize:14 }}>Restricted to authorized personnel</p>
        </div>
        <div style={{ background:"linear-gradient(135deg,#0d0d1f,#111128)", border:"1px solid #ffffff0d", borderRadius:24, padding:36 }}>
          <div style={{ marginBottom:18 }}>
            <label style={S.lbl}>Admin Email</label>
            <input style={S.inp} type="email" placeholder="Admin email address" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key==="Enter" && login()} autoFocus/>
          </div>
          <div style={{ marginBottom:24 }}>
            <label style={S.lbl}>Password</label>
            <input style={S.inp} type="password" placeholder="Admin password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key==="Enter" && login()}/>
          </div>
          {error && (
            <div style={{ background:"#f8717118", border:"1px solid #f8717144", borderRadius:10, padding:"10px 14px", color:"#f87171", fontSize:13, marginBottom:16 }}>
              🔒 {error}
            </div>
          )}
          <button style={{ ...S.btn, opacity: loading?0.6:1 }} onClick={login} disabled={loading}>
            {loading ? "Verifying..." : "Access Admin Panel →"}
          </button>
          <p style={{ textAlign:"center", marginTop:16, color:"#4b5563", fontSize:12 }}>
            Unauthorized access attempts are logged.
          </p>
        </div>
        <p onClick={() => navigate("/")} style={{ textAlign:"center", marginTop:20, color:"#4b5563", fontSize:13, cursor:"pointer" }}>← Back to home</p>
      </div>
    </div>
  );

  // ── Admin Dashboard ─────────────────────────────────────────────────────────
  const freeSearchesUsed = users.reduce((sum, u) => sum + (u.free_used || 0), 0);
  const paidUsers = users.filter(u => u.plan && u.plan !== "free");
  const mrr = stats?.mrr_inr || 0;
  const todaySignups = users.filter(u => {
    const d = new Date(u.created_at || 0);
    return d.toDateString() === new Date().toDateString();
  }).length;

  const metrics = [
    { icon:"👥", label:"Total Signups",        value: stats?.total_users || 0,   sub: `+${todaySignups} today`,                                                                              color:"#00d4aa" },
    { icon:"🔍", label:"Free Searches Used",   value: freeSearchesUsed,           sub: `${stats?.free_users || 0} free users`,                                                               color:"#60a5fa" },
    { icon:"💰", label:"Conversions to Paid",  value: paidUsers.length,           sub: `${stats?.total_users ? Math.round((paidUsers.length/stats.total_users)*100) : 0}% conversion rate`, color:"#a855f7" },
    { icon:"📈", label:"Monthly Revenue",      value: `₹${mrr.toLocaleString()}`, sub: "MRR (estimated)",                                                                                   color:"#f59e0b" },
  ];

  const planBreakdown = [
    { plan:"Free",       count: stats?.free_users||0,             color:"#6b7280", rev:0 },
    { plan:"Starter",    count: stats?.plans?.starter||0,         color:"#00d4aa", rev:(stats?.plans?.starter||0)*999 },
    { plan:"Pro",        count: stats?.plans?.pro||0,             color:"#ff6b35", rev:(stats?.plans?.pro||0)*1999 },
    { plan:"Enterprise", count: stats?.plans?.enterprise||0,      color:"#a855f7", rev:(stats?.plans?.enterprise||0)*3999 },
  ];

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700&family=Syne:wght@900&display=swap" rel="stylesheet"/>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
          <div>
            <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:28, fontWeight:900, margin:0 }}>⚡ Admin Dashboard</h1>
            <p style={{ color:"#6b7280", fontSize:13, margin:"4px 0 0" }}>LeadBot Pro · {ADMIN_EMAIL}</p>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={() => navigate("/dashboard")} style={{ background:"transparent", color:"#9ca3af", border:"1px solid #ffffff22", borderRadius:10, padding:"9px 16px", cursor:"pointer", fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>Dashboard</button>
            <button onClick={logout} style={{ background:"#f8717118", color:"#f87171", border:"1px solid #f8717133", borderRadius:10, padding:"9px 16px", cursor:"pointer", fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>Logout</button>
          </div>
        </div>

        {msg && <div style={{ background:"#00d4aa18", border:"1px solid #00d4aa44", borderRadius:10, padding:"10px 16px", marginBottom:16, color:"#00d4aa", fontSize:14 }}>{msg}</div>}

        {/* 4 Key Metrics */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:24 }}>
          {metrics.map((m,i) => (
            <div key={i} style={S.stat}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ color:"#6b7280", fontSize:12, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>{m.label}</div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:32, fontWeight:900, color:m.color, marginBottom:4 }}>{m.value}</div>
                  <div style={{ color:"#6b7280", fontSize:12 }}>{m.sub}</div>
                </div>
                <span style={{ fontSize:28, opacity:0.5 }}>{m.icon}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Revenue Breakdown */}
        <div style={S.card}>
          <h3 style={{ fontFamily:"'Syne',sans-serif", fontSize:14, fontWeight:900, marginBottom:16, color:"#6b7280", textTransform:"uppercase", letterSpacing:1 }}>Revenue Breakdown</h3>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
            {planBreakdown.map((p,i) => (
              <div key={i} style={{ background:"#050510", border:`1px solid ${p.color}33`, borderRadius:12, padding:16, textAlign:"center" }}>
                <div style={{ color:p.color, fontSize:12, fontWeight:700, textTransform:"uppercase", marginBottom:8 }}>{p.plan}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:26, fontWeight:900, color:p.color, marginBottom:4 }}>{p.count}</div>
                <div style={{ color:"#6b7280", fontSize:11, marginBottom:6 }}>users</div>
                {p.rev > 0 && <div style={{ color:p.color, fontSize:13, fontWeight:700 }}>₹{p.rev.toLocaleString()}/mo</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Users Table */}
        <div style={S.card}>
          <h3 style={{ fontFamily:"'Syne',sans-serif", fontSize:14, fontWeight:900, marginBottom:16, color:"#6b7280", textTransform:"uppercase", letterSpacing:1 }}>All Users ({users.length})</h3>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:"1px solid #ffffff11" }}>
                  {["Email","Plan","Free Used","Searches","Leads","Joined","Actions"].map(h => (
                    <th key={h} style={{ padding:"10px 12px", textAlign:"left", color:"#6b7280", fontSize:11, textTransform:"uppercase", letterSpacing:1, fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u,i) => {
                  const pc = { free:"#6b7280", starter:"#00d4aa", pro:"#ff6b35", enterprise:"#a855f7" }[u.plan||"free"] || "#6b7280";
                  return (
                    <tr key={i} style={{ borderBottom:"1px solid #ffffff06" }}>
                      <td style={{ padding:"12px", color:"#fff" }}>{u.email}</td>
                      <td style={{ padding:"12px" }}>
                        <span style={{ background:pc+"22", color:pc, border:`1px solid ${pc}44`, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>
                          {(u.plan||"free").toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding:"12px", color:"#9ca3af", textAlign:"center" }}>{u.free_used||0}/2</td>
                      <td style={{ padding:"12px", color:"#9ca3af", textAlign:"center" }}>{u.searches||0}</td>
                      <td style={{ padding:"12px", color:"#9ca3af", textAlign:"center" }}>{u.lead_count||0}</td>
                      <td style={{ padding:"12px", color:"#6b7280", fontSize:12 }}>{u.created_at?.slice(0,10)||"—"}</td>
                      <td style={{ padding:"12px" }}>
                        <div style={{ display:"flex", gap:6 }}>
                          {u.plan==="free" ? (
                            <>
                              <button onClick={() => upgradePlan(u.email,"starter")} style={{ background:"#00d4aa22", color:"#00d4aa", border:"1px solid #00d4aa44", borderRadius:6, padding:"4px 8px", fontSize:10, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>Starter</button>
                              <button onClick={() => upgradePlan(u.email,"pro")} style={{ background:"#ff6b3522", color:"#ff6b35", border:"1px solid #ff6b3544", borderRadius:6, padding:"4px 8px", fontSize:10, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>Pro</button>
                              <button onClick={() => upgradePlan(u.email,"enterprise")} style={{ background:"#a855f722", color:"#a855f7", border:"1px solid #a855f744", borderRadius:6, padding:"4px 8px", fontSize:10, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>Ent</button>
                            </>
                          ) : (
                            <button onClick={() => upgradePlan(u.email,"free")} style={{ background:"#f8717122", color:"#f87171", border:"1px solid #f8717144", borderRadius:6, padding:"4px 8px", fontSize:10, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>Revoke</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
